import { Search, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';

interface SearchInputProps {
	onSearch: (searchTerm: string) => void;
	placeholder?: string;
	debounceMs?: number;
	initialValue?: string;
	className?: string;
	disabled?: boolean;
}

export const SearchInput = React.memo(function SearchInput({
	onSearch,
	placeholder = 'Search...',
	debounceMs = 300,
	initialValue = '',
	className = '',
	disabled = false,
}: SearchInputProps) {
	const [value, setValue] = useState(initialValue);

	// Use ref to avoid recreating the debounced function
	const onSearchRef = useRef(onSearch);
	onSearchRef.current = onSearch;

	// Track previous value to prevent unnecessary effects
	const prevValueRef = useRef(value);

	// Effect to handle debounced search - only when value actually changes
	useEffect(() => {
		// Skip if value hasn't changed
		if (prevValueRef.current === value) {
			return;
		}

		prevValueRef.current = value;

		const timeoutId = setTimeout(() => {
			onSearchRef.current(value);
		}, debounceMs);

		return () => clearTimeout(timeoutId);
	}, [value, debounceMs]);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setValue(e.target.value);
	};

	const handleClear = () => {
		setValue('');
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Escape') {
			handleClear();
		}
	};

	return (
		<div className={`relative ${className}`}>
			<div className="relative">
				{/* Search icon */}
				<div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
					<Search className="w-4 h-4 text-gray-400 dark:text-gray-500" />
				</div>

				{/* Input field */}
				<input
					type="text"
					value={value}
					onChange={handleChange}
					onKeyDown={handleKeyDown}
					placeholder={placeholder}
					disabled={disabled}
					className="block py-2 pr-10 pl-10 w-full placeholder-gray-500 text-gray-900 bg-white rounded-md border border-gray-300 transition-colors dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
				/>

				{/* Clear button */}
				{value && !disabled && (
					<div className="flex absolute inset-y-0 right-0 items-center pr-3">
						<button
							type="button"
							onClick={handleClear}
							className="text-gray-400 transition-colors hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
							aria-label="Clear search"
						>
							<X className="w-4 h-4" />
						</button>
					</div>
				)}
			</div>

			{/* Optional: Search suggestions or recent searches could go here */}
		</div>
	);
});

/**
 * Hook for managing search state with debouncing
 */
export function useSearchState(initialValue = '', debounceMs = 300) {
	const [searchTerm, setSearchTerm] = useState(initialValue);
	const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(initialValue);

	useEffect(() => {
		const timeoutId = setTimeout(() => {
			setDebouncedSearchTerm(searchTerm);
		}, debounceMs);

		return () => clearTimeout(timeoutId);
	}, [searchTerm, debounceMs]);

	return {
		searchTerm,
		debouncedSearchTerm,
		setSearchTerm,
		clearSearch: () => setSearchTerm(''),
	};
}
